import { Spacing } from '@standandforce/foundation';
import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

type Breakpoint = 'default' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type ColumnsConfig = number | Partial<Record<Breakpoint, number>>;

/**
 * Gridコンポーネントのプロパティ
 */
export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * グリッドの列数
   * @default 1
   */
  // oxlint-disable-next-line typescript/no-redundant-type-constituents -- 1-6列を主に期待することを明示
  columns?: ColumnsConfig | 1 | 2 | 3 | 4 | 5 | 6;

  /**
   * gridTemplateColumns: グリッドトラックを指定するためのCSSプロパティ。
   * 例: "500px 1fr 200px"
   * 指定がない場合、デフォルトで `repeat(${columns}, 1fr)` が適用されます。
   */
  gridTemplateColumns?: string;

  /**
   * 列間のギャップ
   * @default 4
   */
  columnGap?: keyof typeof Spacing;

  /**
   * 行間のギャップ
   * @default 4
   */
  rowGap?: keyof typeof Spacing;
}

/**
 * グリッドレイアウトを提供するコンポーネント
 * @example
 */
export function Grid({
  columns = 1,
  gridTemplateColumns,
  columnGap = 4,
  rowGap = 4,
  className,
  style,
  children,
  ...props
}: GridProps) {
  const gridStyle = {
    display: 'grid',
    gap: `${Spacing[columnGap]} ${Spacing[rowGap]}`,
    ...(gridTemplateColumns ? { gridTemplateColumns } : {}),
    ...style,
  };

  const gridClassName = cn(
    !gridTemplateColumns && {
      'grid-cols-1':
        (typeof columns === 'number' && columns === 1) ||
        (typeof columns === 'object' && columns.default === 1),
      'grid-cols-2':
        (typeof columns === 'number' && columns === 2) ||
        (typeof columns === 'object' && columns.default === 2),
      'grid-cols-3':
        (typeof columns === 'number' && columns === 3) ||
        (typeof columns === 'object' && columns.default === 3),
      'grid-cols-4':
        (typeof columns === 'number' && columns === 4) ||
        (typeof columns === 'object' && columns.default === 4),
      'grid-cols-5':
        (typeof columns === 'number' && columns === 5) ||
        (typeof columns === 'object' && columns.default === 5),
      'grid-cols-6':
        (typeof columns === 'number' && columns === 6) ||
        (typeof columns === 'object' && columns.default === 6),

      'sm:grid-cols-1': typeof columns === 'object' && columns.sm === 1,
      'sm:grid-cols-2': typeof columns === 'object' && columns.sm === 2,
      'sm:grid-cols-3': typeof columns === 'object' && columns.sm === 3,
      'sm:grid-cols-4': typeof columns === 'object' && columns.sm === 4,
      'sm:grid-cols-5': typeof columns === 'object' && columns.sm === 5,
      'sm:grid-cols-6': typeof columns === 'object' && columns.sm === 6,

      'md:grid-cols-1': typeof columns === 'object' && columns.md === 1,
      'md:grid-cols-2': typeof columns === 'object' && columns.md === 2,
      'md:grid-cols-3': typeof columns === 'object' && columns.md === 3,
      'md:grid-cols-4': typeof columns === 'object' && columns.md === 4,
      'md:grid-cols-5': typeof columns === 'object' && columns.md === 5,
      'md:grid-cols-6': typeof columns === 'object' && columns.md === 6,

      'lg:grid-cols-1': typeof columns === 'object' && columns.lg === 1,
      'lg:grid-cols-2': typeof columns === 'object' && columns.lg === 2,
      'lg:grid-cols-3': typeof columns === 'object' && columns.lg === 3,
      'lg:grid-cols-4': typeof columns === 'object' && columns.lg === 4,
      'lg:grid-cols-5': typeof columns === 'object' && columns.lg === 5,
      'lg:grid-cols-6': typeof columns === 'object' && columns.lg === 6,
    },
    className,
  );

  return (
    <div className={gridClassName} style={gridStyle} {...props}>
      {children}
    </div>
  );
}
